import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  LinearProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Upload as UploadIcon,
  PlayArrow as ProcessIcon,
  FileCopy as FileIcon,
  Link as LinkIcon,
  Storage as StorageIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { fetchEntities, clearError } from '../store/slices/entitySlice';
import { showSuccess, showError, showInfo } from '../store/slices/toastSlice';
import { Entity, EntityStatus } from '../types';

const EntityDetail: React.FC = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { entities, isLoading, error } = useAppSelector((state) => state.entities);

  const [currentEntity, setCurrentEntity] = useState<Entity | null>(null);

  useEffect(() => {
    if (entityId) {
      const entity = entities.find(e => e.id === entityId);
      if (entity) {
        setCurrentEntity(entity);
      } else {
        dispatch(fetchEntities());
      }
    }
  }, [entityId, entities, dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(showError({
        title: 'Error',
        message: error,
        duration: 5000,
      }));
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const getStatusColor = (status: EntityStatus) => {
    switch (status) {
      case EntityStatus.ACTIVE:
        return 'success';
      case EntityStatus.PROCESSING:
        return 'warning';
      case EntityStatus.INACTIVE:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: EntityStatus) => {
    switch (status) {
      case EntityStatus.ACTIVE:
        return <CheckCircleIcon color="success" />;
      case EntityStatus.PROCESSING:
        return <ScheduleIcon color="warning" />;
      case EntityStatus.INACTIVE:
        return <ErrorIcon color="error" />;
      default:
        return <WarningIcon />;
    }
  };

  const handleProcess = () => {
    // TODO: Implement entity processing
    dispatch(showInfo({
      title: 'Coming Soon',
      message: 'Entity processing functionality will be implemented soon.',
      duration: 4000,
    }));
  };

  const handleUploadFiles = () => {
    // TODO: Implement file upload
    dispatch(showInfo({
      title: 'Coming Soon',
      message: 'File upload functionality will be implemented soon.',
      duration: 4000,
    }));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  if (!currentEntity) {
    return (
      <Box>
        <Alert severity="error">Entity not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/entities')}
          sx={{ mr: 2 }}
        >
          Back to Knowledge Bases
        </Button>
        <Typography variant="h4" component="h1">
          {currentEntity.name}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Entity Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    {getStatusIcon(currentEntity.status)}
                    <Chip
                      label={currentEntity.status}
                      color={getStatusColor(currentEntity.status)}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Processing Status
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    {currentEntity.is_processed ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <ScheduleIcon color="warning" />
                    )}
                    <Chip
                      label={currentEntity.is_processed ? 'Processed' : 'Not Processed'}
                      color={currentEntity.is_processed ? 'success' : 'warning'}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1" mt={1}>
                    {currentEntity.description || 'No description available'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Statistics */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Statistics
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <FileIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4" component="div">
                      {currentEntity.file_count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Files
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <LinkIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4" component="div">
                      {currentEntity.url_count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      URLs
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <StorageIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4" component="div">
                      {currentEntity.is_processed ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Processed
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Actions
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={handleUploadFiles}
                >
                  Upload Files
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ProcessIcon />}
                  onClick={handleProcess}
                  disabled={currentEntity.is_processed}
                >
                  Process Entity
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Metadata */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Metadata
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Created"
                    secondary={new Date(currentEntity.created_at).toLocaleDateString()}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Updated"
                    secondary={new Date(currentEntity.updated_at).toLocaleDateString()}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <StorageIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Owner ID"
                    secondary={currentEntity.user_id}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <List dense>
                <ListItem button onClick={handleUploadFiles}>
                  <ListItemIcon>
                    <UploadIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Upload Files" />
                </ListItem>
                <ListItem button onClick={handleProcess}>
                  <ListItemIcon>
                    <ProcessIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Process Entity" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EntityDetail;
